import PropTypes from 'prop-types';
import {
  breakpoints,
  Inline,
  PageBlock,
  Stack,
  Text,
  textColors,
  textVariants,
  vars,
} from '@etg/wings';
import { Notification as EtiNotification, variants } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { useToggle } from '../../../common/custom-hooks/toggle';
import { useBackgroundImage, backgroundImageTypes } from '../../../utils/backgroundImage';
import { getDestinationBackgroundImage } from '../../../utils/destinationBackgroundImage';
import { groupSegmentsByPnr } from '../utils/pnr';
import Title from './ReceiptTitle';

const blockStyles = css`
  position: relative;

  &::before {
    background-image: linear-gradient(0deg, rgb(0 0 0 / 90%), 30%, rgb(0 0 0 / 5%));
    content: '';
    display: grid;
    height: 100%;
    place-content: center;
    position: absolute;
    width: 100%;
  }
`;

const positionStyles = css`
  padding-top: 3rem;
  position: relative;
`;

const segmentsBoxStyles = css`
  background-color: rgb(14 86 130 / 80%);
  border-radius: 0.375rem;
  max-width: 48.75rem;
  padding: 1.25rem 1.5rem 1.5rem;
  width: 100%;
`;

const segmentsStyles = css`
  background-color: #fff;
  border-radius: 5px;
`;

const segmentsHeaderStyles = css`
  align-items: center;
  display: flex;
  font-size: 0.625rem;
  justify-content: space-between;
  padding: 10px 10px 0;

  @media (min-width: ${breakpoints._970}) {
    font-size: 0.75rem;
  }
`;

const segmentsItemStyles = css`
  align-items: center;
  border-bottom: 1px dotted ${vars.colors.divider};
  display: flex;
  font-size: 0.875rem;
  justify-content: space-between;
  margin: 0 10px;
  padding: 10px 0;

  &:last-child {
    border: 0;
  }

  @media (min-width: ${breakpoints._970}) {
    font-size: 1rem;
  }
`;

const referenceStyles = css`
  font-weight: 700;
  text-transform: uppercase;
`;

const ReceiptFlights = ({ destination = {}, shouldDisplayNotification, segments = [] }) => {
  const { isOpen, handleClose } = useToggle(true);
  const { t } = useTranslation();
  const { cssClass } = useBackgroundImage(
    getDestinationBackgroundImage(destination.cityCode),
    backgroundImageTypes.LOCATION,
  );

  const separator = ' - ';
  return (
    <PageBlock className={cx(cssClass, blockStyles)} withPaddingY>
      <Stack className={positionStyles} spacing={24}>
        {shouldDisplayNotification && isOpen && (
          <EtiNotification
            closeButtonDataTestId="receipt-flights-notificationBtn"
            onClose={handleClose}
            variant={variants.INFORMATION}
          >
            <p>{t('Receipt.Flights.NotificationMessage')}</p>
          </EtiNotification>
        )}
        <Inline align="center">
          <Stack className={segmentsBoxStyles}>
            <Title>{t('Receipt.Flights.Heading')}</Title>
            <div>
              <div className={segmentsStyles}>
                <Text
                  className={segmentsHeaderStyles}
                  color={textColors.SECONDARY}
                  variant={textVariants._12}
                >
                  <span>{t('Receipt.Flights.Flights')}</span>{' '}
                  <span>{t('Receipt.Flights.BookingRef')}</span>
                </Text>
                {groupSegmentsByPnr(segments).map((segment, index) => (
                  <div
                    className={segmentsItemStyles}
                    key={`${segment.boundId}${segment.segmentId}`}
                  >
                    <div>
                      <span>{segment.origin.cityName}</span>
                      {separator}
                      <span>{segment.destination.cityName}</span>
                    </div>
                    <div
                      className={referenceStyles}
                      data-testid={`orderPage-ReceiptFlights-${index}-pnr`}
                    >
                      {segment.pnr}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Stack>
        </Inline>
      </Stack>
    </PageBlock>
  );
};

ReceiptFlights.propTypes = {
  destination: PropTypes.shape({
    cityCode: PropTypes.string,
  }),
  segments: PropTypes.arrayOf(PropTypes.object),
  shouldDisplayNotification: PropTypes.bool,
};

export default ReceiptFlights;
